import React from 'react';
import { Input } from '@vehix/portal/components/ui/input';
import { Button } from '@vehix/portal/components/ui/button';
import { BarChartHorizontal, Search, Settings, Table2, X } from 'lucide-react';
import type { ScheduleFilters, ScheduleStatusFilter } from '../../utils/scheduleRows';

export type ScheduleView = 'table' | 'timeline';

interface PipelineSchedulesToolbarProps {
  filters: ScheduleFilters;
  onFiltersChange: (next: ScheduleFilters) => void;
  view: ScheduleView;
  onViewChange: (view: ScheduleView) => void;
  shown: number;
  total: number;
  onOpenSettings: () => void;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: ScheduleStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' }
];

/** Extracted so the JSX stays under max-len; prettier won't wrap a className. */
const SEARCH_ICON_CLASS =
  'absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground';
const CLEAR_BUTTON_CLASS =
  'absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground';

/** Bordered segmented control, matching PipelineRangeFilter's idiom. */
function segmentClass(active: boolean): string {
  const base = 'h-8 rounded-none border-y border-r px-3 text-xs first:rounded-l-md first:border-l';
  const shape = `${base} last:rounded-r-md`;
  const tone = active ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted';
  return `${shape} ${tone}`;
}

// eslint-disable-next-line max-lines-per-function
export const PipelineSchedulesToolbar: React.FC<PipelineSchedulesToolbarProps> = ({
  filters,
  onFiltersChange,
  view,
  onViewChange,
  shown,
  total,
  onOpenSettings
}) => {
  const set = (patch: Partial<ScheduleFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className='flex flex-wrap items-center gap-3'>
      <div className='relative'>
        <Search className={SEARCH_ICON_CLASS} />
        <Input
          value={filters.query}
          onChange={e => set({ query: e.target.value })}
          placeholder='Search name, tenant, zone…'
          aria-label='Search schedules'
          className='h-8 w-[220px] pl-8 pr-7 text-xs'
        />
        {filters.query && (
          <button
            type='button'
            onClick={() => set({ query: '' })}
            aria-label='Clear search'
            className={CLEAR_BUTTON_CLASS}
          >
            <X className='h-3.5 w-3.5' />
          </button>
        )}
      </div>

      <div className='flex'>
        {STATUS_OPTIONS.map(opt => (
          <Button
            key={opt.value}
            type='button'
            variant='ghost'
            size='sm'
            aria-pressed={filters.status === opt.value}
            className={segmentClass(filters.status === opt.value)}
            onClick={() => set({ status: opt.value })}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      <div className='ml-auto flex items-center gap-3'>
        <span className='text-xs text-muted-foreground'>
          {total === 0
            ? 'No schedules'
            : shown === total
              ? `${total} schedule${total === 1 ? '' : 's'}`
              : `Showing ${shown} of ${total} schedules`}
        </span>

        <Button
          type='button'
          variant='outline'
          size='sm'
          className='h-8 text-xs'
          onClick={onOpenSettings}
          title='Notification recipients for all schedules'
        >
          <Settings className='mr-1 h-3.5 w-3.5' />
          Settings
        </Button>

        <div className='flex'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            aria-pressed={view === 'table'}
            title='Table view'
            className={segmentClass(view === 'table')}
            onClick={() => onViewChange('table')}
          >
            <Table2 className='mr-1 h-3.5 w-3.5' />
            Table
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            aria-pressed={view === 'timeline'}
            title='Timeline view — see when schedules run across the day'
            className={segmentClass(view === 'timeline')}
            onClick={() => onViewChange('timeline')}
          >
            <BarChartHorizontal className='mr-1 h-3.5 w-3.5' />
            Timeline
          </Button>
        </div>
      </div>
    </div>
  );
};
